import React from 'react'
import Typography from 'antd/es/typography'
import { themeConstants } from '../../styles/theme-constants'

const { Text: AntText, Title: AntTitle } = Typography

interface CustomTextProps {
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl'
  fontWeight?: number
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  textStyle?: 'normal' | 'italic' | 'strong'
  type?: 'secondary' | 'success' | 'warning' | 'danger'
}

interface CustomTitleProps {
  level?: 1 | 2 | 3 | 4 | 5
  children: React.ReactNode
  className?: string
  textStyle?: string
  style?: React.CSSProperties
}

type TextSize = NonNullable<CustomTextProps['size']>
type TextStyleName = NonNullable<CustomTextProps['textStyle']>

const FONT_SIZE: Record<TextSize, string> = {
  xs: themeConstants.fontSizeXS,
  sm: themeConstants.fontSizeSM,
  base: themeConstants.fontSizeBase,
  lg: themeConstants.fontSizeLG,
  xl: themeConstants.fontSizeXL,
}

const TEXT_STYLE: Record<
  TextStyleName,
  { fontWeight: number; fontStyle: React.CSSProperties['fontStyle'] }
> = {
  normal: { fontWeight: 400, fontStyle: 'normal' },
  italic: { fontWeight: 400, fontStyle: 'italic' },
  strong: { fontWeight: 500, fontStyle: 'normal' },
}

// size × textStyle is a closed set of 15 combinations, so the style objects are
// built once at module load. Rebuilding them per render meant a fresh object for
// every one of the ~600 Text nodes a ranking card view mounts.
const BASE_STYLE = (() => {
  const table = {} as Record<
    TextSize,
    Record<TextStyleName, React.CSSProperties>
  >
  for (const size of Object.keys(FONT_SIZE) as TextSize[]) {
    table[size] = {} as Record<TextStyleName, React.CSSProperties>
    for (const name of Object.keys(TEXT_STYLE) as TextStyleName[]) {
      table[size][name] = {
        fontSize: FONT_SIZE[size],
        fontWeight: TEXT_STYLE[name].fontWeight,
        fontStyle: TEXT_STYLE[name].fontStyle,
      }
    }
  }
  return table
})()

export const Text: React.FC<CustomTextProps> = React.memo(
  ({ size = 'base', children, type, textStyle = 'normal', style, ...rest }) => {
    // fontWeight is part of the public props but was already dead: textStyle
    // always supplies a non-nullish weight. Kept out of the DOM spread.
    const { fontWeight, ...props } = rest
    void fontWeight
    const base = BASE_STYLE[size][textStyle]

    return (
      <AntText
        type={type}
        style={style ? { ...base, ...style } : base}
        {...props}
      >
        {children}
      </AntText>
    )
  }
)

export const Title: React.FC<CustomTitleProps> = React.memo(
  ({ level = 1, children, style, ...props }) => (
    <AntTitle level={level} style={style} {...props}>
      {children}
    </AntTitle>
  )
)

// Export an object that mimics the original Typography component structure
const IdeyaTypography = {
  Text,
  Title,
}

export default IdeyaTypography
